import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { Pagination as PaginationType } from '../types';
import CompanySubscriptionEditModal from './CompanySubscriptionEditModal';
import CompanySubscriptionViewModal from './CompanySubscriptionViewModal';
import Pagination from './Pagination';

// CSS global para sobrescrever margin-top do Pagination apenas neste componente
const paginationOverrideCSS = `
  .pagination-override div[style*="margin-top"] {
    margin-top: 0 !important;
  }
  .pagination-override div {
    margin-top: 0 !important;
  }
`;

// Injetar CSS no head se ainda não existir
if (!document.querySelector('#pagination-override-css')) {
  const style = document.createElement('style');
  style.id = 'pagination-override-css';
  style.textContent = paginationOverrideCSS;
  document.head.appendChild(style);
}

interface CompanySubscriptionsModalProps {
  companyId: number;
  companyName: string;
  onClose: () => void;
  onCreateNew: () => void;
  onCreateTrial: () => void;
}

const CompanySubscriptionsModal: React.FC<CompanySubscriptionsModalProps> = ({
  companyId,
  companyName,
  onClose,
  onCreateNew,
  onCreateTrial
}) => {
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 5,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<number | null>(null);
  const [viewingSubscriptionId, setViewingSubscriptionId] = useState<number | null>(null);
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchSubscriptions();
  }, [companyId, pagination.page, filter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        filter
      });
      
      const response = await api.get(`/companies/${companyId}/subscriptions?${params}`);
      setSubscriptions(response.data.subscriptions || []);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Erro ao carregar assinaturas');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calculateFinalValue = (sub: any) => {
    if (!sub.mrr_value || sub.mrr_value === 0) return '-';
    
    // Precisa dos dados de billing para calcular
    if (!sub.company_billing_state) {
      return 'FALTAM DADOS';
    }

    const mrr = sub.mrr_value;
    const isSimplesNacional = sub.company_billing_simples_nacional === 1;
    
    // CSRF: 4.65% se não for Simples Nacional
    const csrf = isSimplesNacional ? 0 : mrr * 0.0465;
    
    // IR: 1.5% se MRR >= 666
    const ir = mrr >= 666 ? mrr * 0.015 : 0;
    
    let tax;
    if (sub.company_billing_state.trim().toLowerCase() === 'sp') {
      tax = csrf + ir;
    } else {
      // ISS: 5% para outros estados
      tax = mrr * 0.05 + csrf + ir;
    }
    
    return formatCurrency(mrr - tax);
  };

  const formatClosingDay = (day: number | null, activatedAt: string | null) => {
    if (!day) return '-';
    
    const now = new Date();
    let closingDate = new Date(now.getFullYear(), now.getMonth(), day);
    
    // Se a data já passou neste mês, pega o próximo mês
    if (closingDate <= now) {
      closingDate = new Date(now.getFullYear(), now.getMonth() + 1, day);
    }
    
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${day} / ${months[closingDate.getMonth()]}`;
  };

  const getCandidatesDatabaseRole = (role: number) => {
    if (role === 2) return 'Acesso Total';
    if (role === 1) return 'Básico';
    return 'Sem Acesso';
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('Motivo é obrigatório');
      return;
    }
    try {
      await api.put(`/companies/subscriptions/${selectedSubscription.id}/cancel`, { reason: cancelReason });
      toast.success('Assinatura cancelada com sucesso');
      setShowCancelModal(false);
      setCancelReason('');
      setSelectedSubscription(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Erro ao cancelar assinatura');
    }
  };

  const handleDeactivate = async () => {
    try {
      await api.put(`/companies/subscriptions/${selectedSubscription.id}/deactivate`);
      toast.success('Assinatura desativada com sucesso');
      setShowDeactivateModal(false);
      setSelectedSubscription(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Erro ao desativar assinatura');
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Histórico de Planos - {companyName}</h2>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
            <button onClick={onCreateNew} style={styles.createBtn}>
              Nova Assinatura
            </button>
            <button onClick={onCreateTrial} style={styles.trialBtn}>
              Novo Trial
            </button>
            <button onClick={onClose} style={styles.closeBtn}>×</button>
          </div>
        </div>

        <div style={styles.filters}>
          <div style={styles.filterRow}>
            <button 
              onClick={() => handleFilterChange('all')} 
              style={{...styles.filterBtn, ...(filter === 'all' ? styles.filterBtnActive : {})}}
            >
              Todas
            </button>
            <button 
              onClick={() => handleFilterChange('subscriptions')} 
              style={{...styles.filterBtn, ...(filter === 'subscriptions' ? styles.filterBtnActive : {})}}
            >
              SUBSCRIPTIONS
            </button>
            <button 
              onClick={() => handleFilterChange('subscriptions-active')} 
              style={{...styles.filterBtn, ...(filter === 'subscriptions-active' ? styles.filterBtnActive : {})}}
            >
              SUBSCRIPTIONS ATIVAS
            </button>
            <button 
              onClick={() => handleFilterChange('subscriptions-inactive')} 
              style={{...styles.filterBtn, ...(filter === 'subscriptions-inactive' ? styles.filterBtnActive : {})}}
            >
              SUBSCRIPTIONS INATIVAS
            </button>
          </div>
          <div style={styles.filterRow}>
            <button 
              onClick={() => handleFilterChange('trials')} 
              style={{...styles.filterBtn, ...(filter === 'trials' ? styles.filterBtnActive : {})}}
            >
              TRIALS
            </button>
            <button 
              onClick={() => handleFilterChange('trials-active')} 
              style={{...styles.filterBtn, ...(filter === 'trials-active' ? styles.filterBtnActive : {})}}
            >
              TRIALS ATIVOS
            </button>
            <button 
              onClick={() => handleFilterChange('trials-inactive')} 
              style={{...styles.filterBtn, ...(filter === 'trials-inactive' ? styles.filterBtnActive : {})}}
            >
              TRIALS INATIVOS
            </button>
          </div>
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loading}>Carregando...</div>
          ) : subscriptions.length === 0 ? (
            <div style={styles.empty}>Nenhum plano encontrado</div>
          ) : (
            subscriptions.map((sub, index) => (
              <div key={`${sub.id}-${index}`} style={styles.subscriptionCard}>
                <div style={styles.subscriptionHeader}>
                  <div>
                    <span style={styles.subscriptionId}>#{sub.id}</span>
                    <strong style={styles.planName}>{sub.plan_name || 'Plano Customizado'}</strong>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    {sub.active === 1 && <span style={styles.activeBadge}>ATIVO</span>}
                    {sub.trial === 1 && <span style={styles.trialBadge}>TRIAL</span>}
                    {sub.deactivated_at && !sub.canceled_at && <span style={styles.deactivatedBadge}>DESATIVADO</span>}
                    {sub.canceled_at && <span style={styles.canceledBadge}>CANCELADO</span>}
                    <div style={styles.actionMenuContainer}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenActionMenu(openActionMenu === sub.id ? null : sub.id);
                        }}
                        style={styles.actionMenuBtn}
                      >
                        ⋮
                      </button>
                      {openActionMenu === sub.id && (
                        <div style={styles.actionMenu}>
                          <button
                            onClick={() => {
                              setViewingSubscriptionId(sub.id);
                              setOpenActionMenu(null);
                            }}
                            style={styles.actionMenuItem}
                          >
                            Ver Detalhes
                          </button>
                          {sub.active === 1 && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedSubscription(sub);
                                  setShowDeactivateModal(true);
                                  setOpenActionMenu(null);
                                }}
                                style={styles.actionMenuItemDeactivate}
                              >
                                Desativar
                              </button>
                              {sub.trial !== 1 && (
                                <button
                                  onClick={() => {
                                    setSelectedSubscription(sub);
                                    setShowCancelModal(true);
                                    setOpenActionMenu(null);
                                  }}
                                  style={styles.actionMenuItemCancel}
                                >
                                  Cancelar
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={styles.subscriptionDetails}>
                  <div style={styles.financialSection}>
                    <div><strong>Valor bruto (MRR):</strong> {sub.mrr_value === 0 ? 'FREE' : formatCurrency(sub.mrr_value)}</div>
                    <div><strong>Valor final (FATURA):</strong> {calculateFinalValue(sub)}</div>
                    <div><strong>Meio pgto.:</strong> {sub.payment_method || '-'}</div>
                    <div><strong>Vencimento:</strong> {formatClosingDay(sub.closing_day, sub.activated_at)}</div>
                  </div>

                  <div style={styles.datesSection}>
                    <div><strong>Criado em:</strong> {formatDate(sub.created_at)}</div>
                    <div><strong>Activated At:</strong> {formatDate(sub.activated_at)}</div>
                    <div><strong>Deactivated At:</strong> {formatDate(sub.deactivated_at)}</div>
                    {sub.canceled_at && (
                      <div><strong>Cancelado em:</strong> {formatDate(sub.canceled_at)}</div>
                    )}
                  </div>
                  
                  {sub.active === 1 && (
                    <button
                      onClick={() => setEditingSubscriptionId(sub.id)}
                      style={styles.editBtn}
                    >
                      Editar
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        
        <div style={styles.paginationContainer}>
          <div style={styles.paginationInfo}>
            Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
          </div>
          <div className="pagination-override" style={{marginTop: 0}}>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      
      {editingSubscriptionId && (
        <CompanySubscriptionEditModal
          subscriptionId={editingSubscriptionId}
          companyName={companyName}
          onClose={() => setEditingSubscriptionId(null)}
          onSave={() => {
            setEditingSubscriptionId(null);
            fetchSubscriptions();
          }}
        />
      )}
      
      {viewingSubscriptionId && (
        <CompanySubscriptionViewModal
          subscriptionId={viewingSubscriptionId}
          companyName={companyName}
          onClose={() => setViewingSubscriptionId(null)}
        />
      )}
      
      {showCancelModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCancelModal(false)}>
          <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.confirmTitle}>Cancelar Assinatura</h3>
            <p>Informe o motivo do cancelamento:</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Motivo do cancelamento..."
              style={styles.textarea}
              rows={3}
            />
            <div style={styles.confirmButtons}>
              <button onClick={() => setShowCancelModal(false)} style={styles.cancelModalBtn}>
                Voltar
              </button>
              <button onClick={handleCancel} style={styles.confirmBtn}>
                Confirmar Cancelamento
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showDeactivateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowDeactivateModal(false)}>
          <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.confirmTitle}>Desativar Assinatura</h3>
            <p>Tem certeza que deseja desativar esta assinatura?</p>
            <div style={styles.confirmButtons}>
              <button onClick={() => setShowDeactivateModal(false)} style={styles.cancelModalBtn}>
                Cancelar
              </button>
              <button onClick={handleDeactivate} style={styles.confirmBtn}>
                Confirmar Desativação
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
    fontSize: '20px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: colors.textLight,
  },
  createBtn: {
    padding: '8px 16px',
    backgroundColor: colors.secondary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  trialBtn: {
    padding: '8px 16px',
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  content: {
    padding: '20px',
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#ECECEC',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '40px',
    color: colors.textLight,
  },
  empty: {
    textAlign: 'center' as const,
    padding: '40px',
    color: colors.textLight,
  },
  subscriptionCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '16px',
    backgroundColor: 'white',
  },
  subscriptionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${colors.border}`,
  },
  subscriptionId: {
    fontWeight: 'bold',
    color: colors.primary,
    marginRight: '12px',
  },
  planName: {
    fontSize: '16px',
    color: colors.text,
  },
  activeBadge: {
    backgroundColor: colors.success,
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    marginRight: '8px',
  },
  trialBadge: {
    backgroundColor: colors.secondary,
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    marginLeft: '8px',
  },
  deactivatedBadge: {
    backgroundColor: colors.warning,
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    marginLeft: '8px',
  },
  canceledBadge: {
    backgroundColor: colors.error,
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    marginLeft: '8px',
  },
  subscriptionDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  financialSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    fontSize: '14px',
    paddingTop: '12px',
    borderTop: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
    padding: '12px',
    borderRadius: '4px',
  },
  datesSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    fontSize: '14px',
    paddingTop: '12px',
    borderTop: `1px solid ${colors.border}`,
  },
  editBtn: {
    marginTop: '12px',
    padding: '8px 16px',
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  actionMenuContainer: {
    position: 'relative' as const,
  },
  actionMenuBtn: {
    padding: '4px 8px',
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  actionMenu: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    backgroundColor: 'white',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '120px',
    marginTop: '4px',
  },
  actionMenuItem: {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  actionMenuItemCancel: {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.error,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  actionMenuItemDeactivate: {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.warning,
    fontFamily: 'inherit',
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
  },
  confirmModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    width: '400px',
    maxWidth: '90vw',
  },
  confirmTitle: {
    margin: '0 0 16px 0',
    color: colors.primary,
  },
  textarea: {
    width: '100%',
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    marginBottom: '16px',
  },
  confirmButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelModalBtn: {
    padding: '8px 16px',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  confirmBtn: {
    padding: '8px 16px',
    backgroundColor: colors.error,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  filters: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: 'white',
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
    flexWrap: 'wrap' as const,
  },
  filterBtn: {
    padding: '6px 12px',
    backgroundColor: 'white',
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap' as const,
  },
  filterBtnActive: {
    backgroundColor: colors.primary,
    color: 'white',
    borderColor: colors.primary,
  },
  paginationContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '16px 20px',
    borderTop: `1px solid ${colors.border}`,
    backgroundColor: 'white',
    gap: '0px',
  },
  paginationInfo: {
    fontSize: '14px',
    color: colors.textLight,
  },
};

export default CompanySubscriptionsModal;
